/**
 * The whole-document schema.
 *
 * Field-level rules cannot express what makes a document *coherent*: that its root exists, that
 * every parent reference resolves, that no node claims to belong to a different map. Those are
 * cross-field conditions, and they are exactly the ones that turn a file into an editor that
 * hangs or a canvas that renders nothing.
 *
 * The structural checks are delegated to `@orbit-mind/domain`'s `checkIntegrity` rather than
 * restated here. Two implementations of "is this a tree" would drift, and the domain's version
 * is the one the editor trusts at runtime — so it is the one a file has to satisfy to get in.
 */
import * as z from 'zod';
import { checkIntegrity } from '@orbit-mind/domain';
import { edgeSchema, mapNodeSchema, mindMapSchema } from './entities';
import { MAX_REPORTED_ISSUES, parseAt } from './boundary';
import { appError, err, ok } from '@orbit-mind/shared-types';
/**
 * Shape only. Structure is checked separately so the two failures stay distinguishable: a bad
 * field is a malformed file, a broken tree is a corrupted one, and they deserve different
 * recovery offers.
 */
export const mapDocumentShapeSchema = z
    .object({
    map: mindMapSchema,
    nodes: z.record(z.string(), mapNodeSchema),
    edges: z.record(z.string(), edgeSchema),
})
    .strict();
/**
 * Every id must equal the key it is filed under.
 *
 * A mismatch is quiet and destructive: lookups go through the key, so the node answers to one
 * id while believing it has another, and the first write that uses `node.id` files a duplicate.
 */
function keysMatchIds(doc) {
    for (const [key, node] of Object.entries(doc.nodes))
        if (node.id !== key)
            return false;
    for (const [key, edge] of Object.entries(doc.edges))
        if (edge.id !== key)
            return false;
    return true;
}
export const mapDocumentSchema = mapDocumentShapeSchema.refine(keysMatchIds, {
    error: 'every node and edge must be filed under its own id',
});
/**
 * Parses a document and checks it is a coherent tree.
 *
 * `checkPlainText` is on: this runs on import and on restore, where the cost of walking every
 * node's text is irrelevant and the cost of letting search silently return the wrong node is not.
 */
export function parseDocument(value, boundary, context = 'map document') {
    const shape = parseAt(mapDocumentSchema, value, boundary, context);
    if (!shape.ok)
        return shape;
    // `parseAt` has established the field types, so this cast is describing what was checked
    // rather than asserting something new. Zod's inferred optionals differ from the domain's under
    // `exactOptionalPropertyTypes`; `__tests__/drift.test-d.ts` is what keeps the two in step.
    const doc = shape.value;
    const issues = checkIntegrity(doc, { checkPlainText: true });
    if (issues.length === 0)
        return ok(doc);
    const base = appError('MAP_CORRUPTED', `${context} is structurally invalid`, {
        details: {
            boundary,
            context,
            issueCount: issues.length,
            // The id of the first broken node. No document content, so it is safe to log.
            firstPath: issues[0]?.id ?? '',
        },
        // A broken tree does not repair itself on a retry; the answer is recovery, not another go.
        retryable: false,
    });
    return err({
        ...base,
        issues: issues.slice(0, MAX_REPORTED_ISSUES).map((issue) => ({
            path: issue.id,
            message: `${issue.code}: ${issue.detail}`,
        })),
    });
}
/**
 * The `.orbitmind` bundle manifest.
 *
 * Read before anything else in the archive. Its whole job is to answer "can this build open this
 * file?" before a single byte of map data is parsed — so a file from a newer version produces a
 * clear message instead of a wall of field errors, and principle "never overwrite an
 * incompatible file without a backup" has something to act on.
 */
export const orbitmindManifestSchema = z
    .object({
    format: z.literal('orbitmind'),
    formatVersion: z.number().int().positive(),
    /** The app build that wrote it, for diagnostics only. Never used to gate behaviour. */
    generator: z.string().max(200),
    createdAt: z.number().int().nonnegative(),
    mapCount: z.number().int().nonnegative(),
    /** Checksums of the archive entries, so corruption is detected before it is imported. */
    checksums: z.record(z.string(), z.string().regex(/^[0-9a-f]{64}$/)),
})
    .strict();
/** The bundle format this build writes and can read. */
export const SUPPORTED_FORMAT_VERSION = 1;
export function checkFormatVersion(manifest) {
    return {
        readable: manifest.formatVersion <= SUPPORTED_FORMAT_VERSION,
        fromFuture: manifest.formatVersion > SUPPORTED_FORMAT_VERSION,
    };
}
//# sourceMappingURL=document.js.map